import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import {
  WeatherOutlookDayResponse,
  WeatherOutlookResponse,
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-outlook.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { pageFromRight } from '../@commons/animations/animations';

/** Habillage de chaque verdict météo. Le serveur décide, l'app dessine. */
const CONDITION_STYLE: Record<
  string,
  { emoji: string; bg: string; ink: string; bar: string }
> = {
  FROST: { emoji: '❄️', bg: '#E7F0FB', ink: '#1B4C7E', bar: '#7EA8D9' },
  HOT: { emoji: '🔥', bg: '#FDEEE4', ink: '#8A3B12', bar: '#F09A6A' },
  RAIN: { emoji: '🌧️', bg: '#E4EEFB', ink: '#1F4E85', bar: '#6E9BD6' },
  DRY: { emoji: '☀️', bg: '#FDF6E0', ink: '#7A5C09', bar: '#E9C75A' },
  WIND: { emoji: '💨', bg: '#EDF1F1', ink: '#3E5250', bar: '#9BB0AD' },
  MILD: { emoji: '🌤️', bg: '#F1F7F3', ink: '#2C5C46', bar: '#9FC9B2' },
};

/** Seuil serveur d'une pluie qui compte. En dessous, le sol reste sec. */
const RAIN_THRESHOLD_MM = 1;

/** Vent qui mérite d'être signalé au jardin. */
const WIND_NOTABLE_KMH = 30;

/** Une série sèche devient un conseil d'arrosage à partir d'ici. */
const DRY_STREAK_NOTABLE_DAYS = 5;

interface DayView extends WeatherOutlookDayResponse {
  weekday: string;
  dayNumber: string;
  isToday: boolean;
  isPast: boolean;
}

/** Ce qu'il faut retenir de la semaine, et le geste qui va avec. */
interface Verdict {
  condition: string;
  key: string;
  params: Record<string, string | number>;
}

/**
 * Détail météo sur la fenêtre utile au jardin.
 *
 * La carte d'accueil ne montre que le jour même : elle ne répond pas à la
 * seule question qui compte pour arroser ou protéger — « et après ? ».
 *
 * Aucun seuil n'est calculé ici : c'est le serveur qui qualifie chaque
 * journée, avec les mêmes valeurs que les conseils du jour. Recalculer côté
 * front garantirait une divergence tôt ou tard.
 */
@Component({
  selector: 'vw-weather-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HeaderPaddingSafeAreaComponent,
    SkeletonLoaderComponent,
  ],
  templateUrl: './weather-detail.component.html',
  animations: [pageFromRight],
  providers: [{ provide: WeatherApi, useClass: WeatherApi }],
})
export class WeatherDetailComponent implements OnInit {
  private readonly weatherApi = inject(WeatherApi);
  private readonly location = inject(Location);

  outlook: WeatherOutlookResponse | null = null;
  days: DayView[] = [];
  isLoading = true;
  failed = false;

  readonly rainThreshold = RAIN_THRESHOLD_MM;
  readonly windNotable = WIND_NOTABLE_KMH;

  /**
   * Tout ce qui se déduit de la fenêtre est calculé une fois, à l'arrivée des
   * données. Le faire dans des accesseurs les rejouerait à chaque cycle de
   * détection pour un résultat invariable.
   */
  scale: { min: number; max: number } = { min: 0, max: 1 };
  verdict: Verdict | null = null;
  peakDay: DayView | null = null;
  nextRainInDays: number | null = null;

  async ngOnInit(): Promise<void> {
    try {
      this.outlook = await this.weatherApi.getOutlook();
      const todayIso = new Date().toLocaleDateString('en-CA');
      this.days = (this.outlook?.days ?? []).map((d) => {
        const date = new Date(d.date + 'T12:00:00');
        return {
          ...d,
          weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
          dayNumber: String(date.getDate()),
          isToday: d.date === todayIso,
          isPast: d.date < todayIso,
        };
      });
      this.scale = this.computeScale();
      this.peakDay = this.computePeakDay();
      this.nextRainInDays = this.computeNextRainInDays(todayIso);
      this.verdict = this.computeVerdict();
    } catch {
      this.failed = true;
    } finally {
      this.isLoading = false;
    }
  }

  goBack(): void {
    this.location.back();
  }

  style(condition: string) {
    return CONDITION_STYLE[condition] ?? CONDITION_STYLE['MILD'];
  }

  /**
   * L'échelle de température de la fenêtre, arrondie au degré.
   *
   * Une échelle commune à tous les jours est ce qui rend les barres
   * comparables : c'est elle qui fait voir la canicule monter puis retomber.
   * Une échelle par ligne donnerait huit barres identiques.
   */
  private computeScale(): { min: number; max: number } {
    const mins = this.days
      .map((d) => d.tempMin)
      .filter((t): t is number => t !== null);
    const maxs = this.days
      .map((d) => d.tempMax)
      .filter((t): t is number => t !== null);
    if (!mins.length || !maxs.length) return { min: 0, max: 1 };
    const min = Math.floor(Math.min(...mins));
    const max = Math.ceil(Math.max(...maxs));
    // Une fenêtre plate ne doit pas diviser par zéro.
    return max > min ? { min, max } : { min, max: min + 1 };
  }

  /** Départ de la barre du jour, en % de l'échelle de la fenêtre. */
  bandLeft(day: DayView): number {
    if (day.tempMin === null) return 0;
    const { min, max } = this.scale;
    return ((day.tempMin - min) / (max - min)) * 100;
  }

  /** Largeur de la barre : l'amplitude nuit → après-midi. */
  bandWidth(day: DayView): number {
    if (day.tempMin === null || day.tempMax === null) return 0;
    const { min, max } = this.scale;
    const width = ((day.tempMax - day.tempMin) / (max - min)) * 100;
    // Sous 3 %, la barre disparaît et la ligne semble vide.
    return Math.max(width, 3);
  }

  /**
   * Dans combien de jours la prochaine pluie ? `null` si rien n'est annoncé.
   *
   * Un cumul seul — « 16 mm à venir » — ne dit pas s'il faut arroser ce soir
   * ou attendre. C'est l'échéance qui décide du geste.
   */
  private computeNextRainInDays(todayIso: string): number | null {
    const next = this.days.find(
      (d) => d.date >= todayIso && (d.precipitation ?? 0) >= RAIN_THRESHOLD_MM,
    );
    if (!next) return null;
    const start = new Date(todayIso + 'T12:00:00').getTime();
    const target = new Date(next.date + 'T12:00:00').getTime();
    return Math.round((target - start) / 86_400_000);
  }

  /** Le jour le plus chaud qui reste à venir : c'est celui qu'on prépare. */
  private computePeakDay(): DayView | null {
    const upcoming = this.days.filter((d) => !d.isPast && d.tempMax !== null);
    if (!upcoming.length) return null;
    return upcoming.reduce((hottest, d) =>
      (d.tempMax ?? 0) > (hottest.tempMax ?? 0) ? d : hottest,
    );
  }

  /**
   * Le verdict de la semaine : une phrase, et le geste qu'elle implique.
   *
   * L'écran donnait la matière et laissait conclure. Or la conclusion est
   * précisément ce que l'app sait faire : le gel passe devant la chaleur, la
   * chaleur devant la pluie, et une longue série sèche parle d'elle-même.
   */
  private computeVerdict(): Verdict | null {
    const upcoming = this.days.filter((d) => !d.isPast);
    const label = (d: DayView) => `${d.weekday} ${d.dayNumber}`;

    const frost = upcoming.find((d) => d.condition === 'FROST');
    if (frost) {
      return {
        condition: 'FROST',
        key: 'weather.detail.next.FROST',
        params: { day: label(frost) },
      };
    }

    const hot = upcoming.filter((d) => d.condition === 'HOT');
    if (hot.length > 1) {
      return {
        condition: 'HOT',
        key: 'weather.detail.next.HOT-multi',
        params: { count: hot.length, day: label(hot[hot.length - 1]) },
      };
    }
    if (hot.length === 1) {
      return {
        condition: 'HOT',
        key: 'weather.detail.next.HOT',
        params: { day: label(hot[0]) },
      };
    }

    const rain = upcoming.find((d) => d.condition === 'RAIN');
    if (rain) {
      return {
        condition: 'RAIN',
        key: 'weather.detail.next.RAIN',
        params: { day: label(rain) },
      };
    }

    const streak = this.outlook?.dryStreak ?? 0;
    if (streak >= DRY_STREAK_NOTABLE_DAYS) {
      return {
        condition: 'DRY',
        key: 'weather.detail.next.DRY-streak',
        params: { count: streak },
      };
    }

    return null;
  }
}
